"use client";

import { useEffect } from 'react';
import { CheckIcon, ExclamationTriangleIcon, InformationCircleIcon, XCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { IconButton } from './Button';
import { academicDecisionFeedbackStateMeta, type AcademicDecisionFeedbackState } from './OperationalPage';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastProps {
  message: string;
  /** Existing outcome vocabulary retained for current callers. */
  type?: ToastType;
  /** Optional shared academic-decision vocabulary for new callers. */
  semanticState?: AcademicDecisionFeedbackState;
  isVisible: boolean;
  onClose: () => void;
  duration?: number;
}

export default function Toast({ message, type = 'info', semanticState, isVisible, onClose, duration = 3000 }: ToastProps) {
  useEffect(() => {
    if (!isVisible || duration <= 0) return;
    const timer = window.setTimeout(onClose, duration);
    return () => window.clearTimeout(timer);
  }, [duration, isVisible, onClose]);
  if (!isVisible) return null;
  const toastType = semanticState ? academicDecisionFeedbackStateMeta[semanticState].toastType : type;
  const content = {
    success: { Icon: CheckIcon, classes: 'border-emerald-300 bg-emerald-50 text-emerald-950 dark:border-emerald-400/25 dark:bg-emerald-950 dark:text-emerald-50', label: 'Success' },
    error: { Icon: XCircleIcon, classes: 'border-rose-300 bg-rose-50 text-rose-950 dark:border-rose-400/25 dark:bg-rose-950 dark:text-rose-50', label: 'Error' },
    warning: { Icon: ExclamationTriangleIcon, classes: 'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-400/25 dark:bg-amber-950 dark:text-amber-50', label: 'Warning' },
    info: { Icon: InformationCircleIcon, classes: 'border-sky-300 bg-sky-50 text-sky-950 dark:border-sky-400/25 dark:bg-sky-950 dark:text-sky-50', label: 'Information' },
  }[toastType];
  const { Icon } = content;
  const label = semanticState ? academicDecisionFeedbackStateMeta[semanticState].label : content.label;
  return <div className="fixed right-4 top-4 z-50 max-w-[calc(100vw-2rem)]" role={toastType === 'error' ? 'alert' : 'status'} aria-live={toastType === 'error' ? 'assertive' : 'polite'}><div className={`flex min-w-64 items-center gap-3 rounded-card border p-3 shadow-e2 ${content.classes}`}><Icon aria-hidden="true" className="h-5 w-5 shrink-0" /><div className="min-w-0 flex-1"><p className="text-xs font-bold uppercase tracking-wide">{label}</p><p className="text-sm">{message}</p></div><IconButton label="Dismiss notification" variant="quiet" className="shrink-0" onClick={onClose}><XMarkIcon className="h-4 w-4" /></IconButton></div></div>;
}
